import type { DOMAttributes } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import { Text } from '../ui';

export type UseDragResult = Required<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Pick<DOMAttributes<any>, 'onDragStart' | 'onDragEnd'>
> & {
  draggable: true;
};

export type UseDragProps = {
  item: DragItem;
};

export type DragItem = {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & Record<string, any>;

export function useDrag({ item }: UseDragProps): UseDragResult {
  return {
    draggable: true,
    onDragStart: useCallback(
      (e: React.DragEvent) => {
        currentItems[item.type] = item;
        e.dataTransfer.setData(item.type, JSON.stringify(item));
      },
      [item],
    ),
    onDragEnd: useCallback(() => {
      currentItems = {};
    }, []),
  };
}

export type UseDropResult = {
  props: Pick<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    DOMAttributes<any>,
    'onDragEnter' | 'onDrop' | 'onDragLeave' | 'onDragOver'
  >;
  isDragOver: boolean;
};

/** This allows reading the current item inside the application at any time, outside the drop handler. */
let currentItems: { [key: string]: DragItem } = {};
let dragElement: Element | null;
export const setDragData = (e: React.DragEvent, data: DragItem): void => {
  e.dataTransfer.setData(data.type, JSON.stringify(data));
  currentItems[data.type] = data;
  dragElement = e.currentTarget;
};

/** Checks a currently dragged item from our own drag item store. */
export function peekDragData<T>(
  _e: React.DragEvent,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parser: (data: any) => T,
): T | null {
  for (const item of Object.values(currentItems)) {
    try {
      const parsed = parser(item);
      return parsed;
    } catch {
      continue;
    }
  }
  return null;
}

/** Parses dropped item from th event. */
export function getDropData<T>(
  e: React.DragEvent,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parser: (data: any) => T,
): T | null {
  for (const t of e.dataTransfer.types) {
    try {
      const parsed = parser(JSON.parse(e.dataTransfer.getData(t)));
      return parsed;
    } catch (err) {
      console.warn(
        'Failed to parse drag data for',
        e.dataTransfer.getData(t),
        err,
      );
    }
  }
  return null;
}

export type UseDropProps = UseDropResult['props'] & {
  /** Triggered when drag is over, regardless of accept status.
   * Only triggers once per enter, no additional triggeres caused by child elements. */
  onDragEnter?: UseDropResult['props']['onDragEnter'];
  /** Triggered when drag is no longer over, regardless of accept status.
   * Only triggers once per leave, no additional triggeres caused by child elements. */
  onDragLeave?: UseDropResult['props']['onDragLeave'];
  /** Triggered while drag is over ONLY when accept status = true.
   * When not accepted, the event is propagated to parents in case one is also listening, if accepted then event stops here. */
  onDragOver?: UseDropResult['props']['onDragOver'];
  /** Triggered on drop ONLY when accept status = true.
   * When not accepted, the event is propagated to parents in case one is also listening, if accepted then event stops here. */
  onDrop?: UseDropResult['props']['onDrop'];
  /** Define a list of data types to accept for dropping or more fine grained control with a function.
   * When undefined, everything is accepted. */
  accept?: string[] | ((e: React.DragEvent) => boolean);
};

/** It's recommended that props is memoized */
export function useDrop(props: UseDropProps): UseDropResult {
  const dragOverCounter = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);

  const accepts = useCallback(
    (e: React.DragEvent) => {
      const accept = props.accept;

      if (!accept) {
        return true;
      }
      if (Array.isArray(accept)) {
        return e.dataTransfer.types.some((t) => accept.includes(t));
      } else {
        return accept(e);
      }
    },
    [props],
  );

  const onDragEnter: UseDropResult['props']['onDragEnter'] = useCallback(
    (e: React.DragEvent) => {
      dragOverCounter.current += 1;
      if (dragOverCounter.current === 1) {
        if (accepts(e)) {
          setIsDragOver(true);
        }
        props.onDragEnter?.(e);
      }
    },
    [accepts, props],
  );

  const onDrop: UseDropResult['props']['onDrop'] = useCallback(
    (e: React.DragEvent) => {
      dragOverCounter.current = 0;
      if (accepts(e)) {
        props.onDrop?.(e);
        e.stopPropagation();
        e.preventDefault();
      }
      setIsDragOver(false);
      setDropEffect('none', '', true);
    },
    [accepts, props],
  );

  const onDragLeave: UseDropResult['props']['onDragLeave'] = useCallback(
    (e: React.DragEvent) => {
      dragOverCounter.current -= 1;
      if (dragOverCounter.current === 0) {
        setIsDragOver(false);
        props.onDragLeave?.(e);
      }
    },
    [props],
  );

  const onDragOver: UseDropResult['props']['onDragOver'] = useCallback(
    (e: React.DragEvent) => {
      if (accepts(e)) {
        setIsDragOver(true);
        props.onDragOver?.(e);
      } else {
        setIsDragOver(false);
      }
      e.stopPropagation();
      e.preventDefault();
    },
    [accepts, props],
  );

  return {
    isDragOver,
    props: {
      onDragEnter,
      onDragLeave,
      onDrop,
      onDragOver,
    },
  };
}

export function setDropEffectOnElement(
  element: Element,
  effect: 'link' | 'copy' | 'move' | 'none',
) {
  switch (effect) {
    case 'link':
      if (!element.classList.contains('drop-link')) {
        element.classList.add('drop-link');
      }
      break;
    case 'copy':
      if (!element.classList.contains('drop-copy')) {
        element.classList.add('drop-copy');
      }
      break;
    case 'move':
      if (!element.classList.contains('drop-move')) {
        element.classList.add('drop-move');
      }
      break;
    case 'none':
      break;
  }
  if (effect !== 'link' && element.classList.contains('drop-link')) {
    element.classList.remove('drop-link');
  }
  if (effect !== 'copy' && element.classList.contains('drop-copy')) {
    element.classList.remove('drop-copy');
  }
  if (effect !== 'move' && element.classList.contains('drop-move')) {
    element.classList.remove('drop-move');
  }
}

export function setDropEffect(
  effect: 'link' | 'copy' | 'move' | 'none',
  description: string,
  isEnd?: boolean,
) {
  setDropEffectOnElement(document.body, effect);
  if (dragElement) {
    setDropEffectOnElement(dragElement, effect);
  }
  const descriptionElement = document.querySelector<HTMLSpanElement>(
    '#drag-drop-description',
  );
  if (descriptionElement && descriptionElement.innerText !== description) {
    descriptionElement.innerText = description;
    const display = description ? 'block' : 'none';
    const container = descriptionElement.parentElement?.parentElement;
    if (container && container.style.display !== display) {
      container.style.display = display;
    }
  }
  if (isEnd) {
    dragElement = null;
  }
}

const descriptionStyles: React.CSSProperties = {
  pointerEvents: 'none',
  position: 'absolute',
  bottom: '52px',
  zIndex: 1,
  right: '10px',
  display: 'none',
  backgroundColor: 'white',
  border: '1px solid black',
  borderRadius: '5px',
  padding: '5px',
};
export const DragDropDescription = React.memo(
  () => {
    return (
      <div style={descriptionStyles}>
        <Text>
          <span id="drag-drop-description" />
        </Text>
      </div>
    );
  },
  () => true,
);
